import { X } from 'lucide-react'
import { useState } from 'react'
import type { FormEvent } from 'react'

import { ApiError } from '@/api/client'
import { PageHeader } from '@/app/layouts/PageHeader'
import { Button } from '@/components/ui/Button'
import { EmptyState } from '@/components/ui/EmptyState'
import { CenteredSpinner } from '@/components/ui/Spinner'
import {
  SettingsSection,
  useAddMutedWord,
  useMutedWords,
  useRemoveMutedWord,
} from '@/features/settings'
import { useT } from '@/i18n'
import { useFormat } from '@/lib/format'

const MAX_LENGTH = 100

export function SettingsMutedWordsPage() {
  const { relativeTime } = useFormat()
  const t = useT()
  const [phrase, setPhrase] = useState('')
  const words = useMutedWords()
  const add = useAddMutedWord()
  const remove = useRemoveMutedWord()
  const error = add.error instanceof ApiError ? add.error : null

  const onSubmit = (event: FormEvent) => {
    event.preventDefault()
    if (!phrase.trim()) return
    add.mutate(phrase, { onSuccess: () => setPhrase('') })
  }

  return (
    <>
      <PageHeader title={t('mutedWords.title')} back />

      <SettingsSection
        title={t('mutedWords.add')}
        description={
          <>
            {t('mutedWords.addDesc1')}
            {t('mutedWords.addDesc2')}
          </>
        }
      >
        <form onSubmit={onSubmit} className="flex gap-2" noValidate>
          <label htmlFor="muted-word" className="sr-only">
            {t('mutedWords.label')}
          </label>
          <input
            id="muted-word"
            name="phrase"
            value={phrase}
            maxLength={MAX_LENGTH}
            onChange={(event) => setPhrase(event.target.value)}
            placeholder={t('mutedWords.placeholder')}
            aria-invalid={Boolean(error)}
            aria-describedby={error ? 'muted-word-error' : undefined}
            className="min-w-0 flex-1 rounded-md border border-line-strong bg-surface px-3 py-2 text-[15px] outline-none focus:border-brand-ink"
          />
          <Button type="submit" loading={add.isPending} disabled={!phrase.trim()}>
            {t('mutedWords.add')}
          </Button>
        </form>
        {error && (
          <p id="muted-word-error" role="alert" className="mt-2 text-sm text-danger">
            {error.message}
          </p>
        )}
        <p className="mt-3 text-[13px] text-ink-muted">
          {t('mutedWords.matchNote')} {t('mutedWords.scopeNote')}
        </p>
      </SettingsSection>

      {words.isPending && <CenteredSpinner />}
      {words.data?.length === 0 && (
        <EmptyState title={t('mutedWords.empty')} description={t('mutedWords.emptyDesc')} />
      )}
      {words.data && words.data.length > 0 && (
        <ul aria-label={t('mutedWords.listLabel')}>
          {words.data.map((word) => (
            <li key={word.id} className="flex items-center gap-3 border-b border-line px-4 py-3">
              <div className="min-w-0 flex-1">
                <p className="truncate text-[15px] font-bold">{word.phrase}</p>
                <p className="text-[13px] text-ink-muted">
                  {t('mutedWords.addedAt', { time: relativeTime(word.created_at) })}
                </p>
              </div>
              <button
                type="button"
                onClick={() => remove.mutate(word.id)}
                aria-label={t('mutedWords.remove', { phrase: word.phrase })}
                className="rounded-md p-2 text-ink-muted hover:bg-surface-hover hover:text-danger"
              >
                <X className="size-5" />
              </button>
            </li>
          ))}
        </ul>
      )}
    </>
  )
}
